'use client';

import React, { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { Search, X, ArrowRight, Tag, Sparkles } from 'lucide-react';
import { searchPrompts } from '@/data/prompts';
import { categories } from '@/data/categories';
import type { Prompt } from '@/data/prompts';
import type { Category } from '@/data/categories';

interface SearchBarProps {
  variant?: 'hero' | 'inline';
  placeholder?: string;
  autoFocus?: boolean;
}

const popularSearches = [
  'romantic couple', 'birthday portrait', 'fantasy forest', 'product photography',
  'wedding', 'cyberpunk', 'newborn baby', 'golden hour',
];

export default function SearchBar({
  variant = 'inline',
  placeholder = 'Search prompts, categories, styles...',
  autoFocus = false,
}: SearchBarProps) {
  const [query, setQuery] = useState('');
  const [focused, setFocused] = useState(false);
  const [promptResults, setPromptResults] = useState<Prompt[]>([]);
  const [categoryResults, setCategoryResults] = useState<Category[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const router = useRouter();

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  useEffect(() => {
    if (query.trim().length < 2) {
      setPromptResults([]);
      setCategoryResults([]);
      return;
    }
    const q = query.toLowerCase();
    setPromptResults(searchPrompts(query).slice(0, 4));
    setCategoryResults(
      categories.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          c.description.toLowerCase().includes(q) ||
          c.group.toLowerCase().includes(q)
      ).slice(0, 3)
    );
  }, [query]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setFocused(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      setFocused(false);
      router.push(`/prompt?search=${encodeURIComponent(query.trim())}`);
    }
  };

  const handleClear = () => {
    setQuery('');
    inputRef.current?.focus();
  };

  const hasResults = promptResults.length > 0 || categoryResults.length > 0;
  const showDropdown = focused && (query.length >= 2 ? hasResults : true);

  return (
    <div ref={containerRef} className={`relative ${variant === 'hero' ? 'w-full max-w-2xl' : 'w-full'}`}>
      <form onSubmit={handleSearch} role="search">
        <label htmlFor="prompt-search" className="sr-only">Search AI image prompts</label>
        <div className={`flex items-center gap-3 rounded-2xl border-2 transition-all duration-200 bg-white ${
          focused
            ? 'border-primary shadow-lg shadow-primary/10'
            : 'border-border hover:border-primary/40'
        } ${variant === 'hero' ? 'px-5 py-3.5' : 'px-4 py-2.5'}`}>
          <Search size={variant === 'hero' ? 20 : 16} className={`flex-shrink-0 transition-colors ${focused ? 'text-primary' : 'text-muted-foreground'}`} />
          <input
            ref={inputRef}
            id="prompt-search"
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => setFocused(true)}
            placeholder={placeholder}
            autoComplete="off"
            className={`flex-1 bg-transparent outline-none text-foreground placeholder:text-muted-foreground ${variant === 'hero' ? 'text-base' : 'text-sm'}`}
          />
          {query && (
            <button
              type="button"
              onClick={handleClear}
              className="p-1 rounded-full hover:bg-muted transition-colors flex-shrink-0"
              aria-label="Clear search"
            >
              <X size={14} className="text-muted-foreground" />
            </button>
          )}
          {variant === 'hero' && (
            <button
              type="submit"
              className="btn-primary text-sm px-4 py-2 flex-shrink-0"
              aria-label="Search"
            >
              Search
            </button>
          )}
        </div>
      </form>

      {/* Dropdown */}
      {showDropdown && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl border border-border shadow-lg z-50 overflow-hidden animate-fade-in">
          {query.length < 2 ? (
            <div className="p-4">
              <p className="text-xs text-muted-foreground font-medium mb-3 uppercase tracking-wider">Popular Searches</p>
              <div className="flex flex-wrap gap-2">
                {popularSearches.map((term) => (
                  <button
                    key={`search-suggest-${term}`}
                    onClick={() => {
                      setQuery(term);
                    }}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-muted text-sm text-muted-foreground hover:bg-accent hover:text-primary transition-colors"
                  >
                    <Sparkles size={11} />
                    {term}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div>
              {categoryResults.length > 0 && (
                <div className="p-3 border-b border-border">
                  <p className="text-xs text-muted-foreground font-medium mb-2 px-1 uppercase tracking-wider">Categories</p>
                  {categoryResults.map((cat) => (
                    <button
                      key={`search-cat-${cat.slug}`}
                      onClick={() => {
                        setFocused(false);
                        router.push(`/categories/${cat.slug}`);
                      }}
                      className="w-full flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-muted transition-colors text-left"
                    >
                      <span className="text-xl">{cat.emoji}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground">{cat.name}</p>
                        <p className="text-xs text-muted-foreground">{cat.promptCount} prompts</p>
                      </div>
                      <Tag size={12} className="text-muted-foreground flex-shrink-0" />
                    </button>
                  ))}
                </div>
              )}
              {promptResults.length > 0 && (
                <div className="p-3">
                  <p className="text-xs text-muted-foreground font-medium mb-2 px-1 uppercase tracking-wider">Prompts</p>
                  {promptResults.map((prompt) => (
                    <button
                      key={`search-prompt-${prompt.id}`}
                      onClick={() => {
                        setFocused(false);
                        router.push(`/prompt/${prompt.slug}`);
                      }}
                      className="w-full flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-muted transition-colors text-left"
                    >
                      <Sparkles size={14} className="text-primary flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground line-clamp-1">{prompt.title}</p>
                        <p className="text-xs text-muted-foreground">{prompt.style}</p>
                      </div>
                      <ArrowRight size={12} className="text-muted-foreground flex-shrink-0" />
                    </button>
                  ))}
                </div>
              )}
              {!hasResults && (
                <div className="p-6 text-center">
                  <p className="text-sm text-muted-foreground">No results for "{query}"</p>
                  <p className="text-xs text-muted-foreground mt-1">Try a different keyword or browse categories</p>
                </div>
              )}
              <div className="p-3 border-t border-border">
                <button
                  onClick={handleSearch}
                  className="w-full flex items-center justify-center gap-2 py-2 rounded-lg bg-muted hover:bg-accent text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
                >
                  <Search size={14} />
                  Search all results for "{query}"
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}